import { XIcon } from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Heading,
  IconButton,
  Select,
  Spinner,
  Text,
  TextField,
} from "@radix-ui/themes";

import type { Order } from "~/v2/api/rest/account";
import { Field } from "~/v3-poc/components/Field";
import type { OrderReplacementController } from "./OrderReplacementDialog.controller";
import {
  formatOrderLabel,
  getOrderSideBadgeColor,
  ORDERS_UNAVAILABLE_VALUE,
} from "./OrdersWidget.utils";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const formatLastPrice = (value: number | undefined) =>
  typeof value === "number" && Number.isFinite(value)
    ? currencyFormatter.format(value)
    : undefined;

export interface OrderReplacePanelProps {
  order?: Order | null;
  controller?: OrderReplacementController;
  isReplacing: boolean;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  eligibilityDisabledReason?: string;
  assetName?: string;
  lastPrice?: number;
  onRetry?: () => void;
  onClose: () => void;
}

const ReplacePanelHeader = ({
  isReplacing,
  onClose,
  orderId,
  symbol,
}: {
  isReplacing: boolean;
  onClose: () => void;
  orderId?: string;
  symbol?: string;
}) => (
  <Flex align="start" justify="between" gap="2">
    <Flex direction="column" minWidth="0">
      <Heading as="h3" size="3">
        Edit order
      </Heading>
      {orderId ? (
        <Text size="1" color="gray" truncate>
          #{orderId}
          {symbol ? ` · ${symbol}` : ""}
        </Text>
      ) : null}
    </Flex>
    <IconButton
      type="button"
      size="1"
      variant="ghost"
      color="gray"
      aria-label="Close editor"
      disabled={isReplacing}
      onClick={onClose}
    >
      <XIcon aria-hidden size={14} />
    </IconButton>
  </Flex>
);

const ReplacePanelAsset = ({
  order,
  assetName,
  lastPrice,
}: {
  order: Order;
  assetName?: string;
  lastPrice?: number;
}) => {
  const symbol = order.symbol?.trim() || ORDERS_UNAVAILABLE_VALUE;
  const formattedLastPrice = formatLastPrice(lastPrice);
  const orderType = formatOrderLabel(order.order_type || order.type);

  return (
    <Flex direction="column" gap="2">
      <Flex align="start" justify="between" gap="3">
        <Box minWidth="0">
          <Text as="div" size="5" weight="bold" truncate>
            {symbol}
          </Text>
          {assetName ? (
            <Text as="p" m="0" size="1" color="gray" truncate>
              {assetName}
            </Text>
          ) : null}
        </Box>
        {formattedLastPrice ? (
          <Text size="5" weight="bold" className="atd-numeric" wrap="nowrap">
            {formattedLastPrice}
          </Text>
        ) : null}
      </Flex>
      <Flex gap="1" wrap="wrap">
        <Badge color={getOrderSideBadgeColor(order.side)} variant="soft">
          {formatOrderLabel(order.side)}
        </Badge>
        <Badge color="gray" variant="soft">
          {orderType}
        </Badge>
      </Flex>
    </Flex>
  );
};

const ReplacePanelStatus = ({
  isLoading,
  isError,
  errorMessage,
  eligibilityDisabledReason,
  onRetry,
}: Pick<
  OrderReplacePanelProps,
  | "isLoading"
  | "isError"
  | "errorMessage"
  | "eligibilityDisabledReason"
  | "onRetry"
>) => {
  if (isLoading) {
    return (
      <Flex align="center" gap="2" py="4" role="status">
        <Spinner size="1" />
        <Text size="2" color="gray">
          Loading current order…
        </Text>
      </Flex>
    );
  }
  if (isError) {
    return (
      <Callout.Root color="red" role="alert">
        <Callout.Text>
          {errorMessage ?? "Current order details could not be loaded."}
        </Callout.Text>
        {onRetry ? (
          <Button
            type="button"
            size="1"
            variant="soft"
            color="red"
            onClick={onRetry}
          >
            Retry
          </Button>
        ) : null}
      </Callout.Root>
    );
  }
  if (eligibilityDisabledReason) {
    return (
      <Callout.Root color="orange" role="alert">
        <Callout.Text>{eligibilityDisabledReason}</Callout.Text>
      </Callout.Root>
    );
  }
  return null;
};

const ReplaceDecimalField = ({
  label,
  error,
  value,
  onChange,
}: {
  label: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <Field label={label} error={error}>
    {(control) => (
      <TextField.Root
        {...control}
        className="atd-numeric"
        type="number"
        min="0"
        step="any"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    )}
  </Field>
);

const ReplacePanelForm = ({
  order,
  controller,
  isReplacing,
  errorMessage,
}: {
  order?: Order | null;
  controller: OrderReplacementController;
  isReplacing: boolean;
  errorMessage?: string;
}) => {
  const showLimitPrice =
    controller.applicablePriceFields.includes("limitPrice");
  const showStopPrice = controller.applicablePriceFields.includes("stopPrice");
  const quantityLabel =
    order?.asset_class === "us_equity" ? "Quantity (full shares)" : "Quantity";

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        controller.onSubmit();
      }}
    >
      <Flex direction="column" gap="3">
        <Grid columns="2" gap="3">
          <ReplaceDecimalField
            label={quantityLabel}
            error={controller.errors.qty}
            value={controller.values.qty}
            onChange={(value) => controller.onFieldChange("qty", value)}
          />
          {showLimitPrice ? (
            <ReplaceDecimalField
              label="Limit price"
              error={controller.errors.limitPrice}
              value={controller.values.limitPrice}
              onChange={(value) =>
                controller.onFieldChange("limitPrice", value)
              }
            />
          ) : null}
          {showStopPrice ? (
            <ReplaceDecimalField
              label="Stop price"
              error={controller.errors.stopPrice}
              value={controller.values.stopPrice}
              onChange={(value) => controller.onFieldChange("stopPrice", value)}
            />
          ) : null}
        </Grid>

        <Field label="Time in force">
          {(control) => (
            <Select.Root
              value={controller.values.timeInForce}
              onValueChange={(value) =>
                controller.onFieldChange("timeInForce", value)
              }
            >
              <Select.Trigger
                {...control}
                className="w-full"
                aria-label="Time in force"
              />
              <Select.Content>
                {controller.timeInForceOptions.map((option) => (
                  <Select.Item key={option.value} value={option.value}>
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          )}
        </Field>

        {showLimitPrice ? (
          <Flex
            align="center"
            justify="between"
            gap="2"
            pt="3"
            className="border-[color:var(--gray-a5)] border-t"
          >
            <Text size="2" color="gray">
              Est. cost
            </Text>
            <Text size="4" weight="bold" className="atd-numeric">
              {controller.estimatedCost}
            </Text>
          </Flex>
        ) : null}

        {controller.errors.form ? (
          <Text size="1" color="red" role="alert">
            {controller.errors.form}
          </Text>
        ) : null}
        {errorMessage ? (
          <Text size="1" color="red" role="alert">
            {errorMessage}
          </Text>
        ) : null}

        <Button
          type="submit"
          size="3"
          disabled={isReplacing}
          loading={isReplacing}
        >
          Replace order
        </Button>
      </Flex>
    </form>
  );
};

export const OrderReplacePanelPresentation = ({
  order,
  controller,
  isReplacing,
  isLoading = false,
  isError = false,
  errorMessage,
  eligibilityDisabledReason,
  assetName,
  lastPrice,
  onRetry,
  onClose,
}: OrderReplacePanelProps) => {
  const showForm =
    Boolean(controller) && !isLoading && !isError && !eligibilityDisabledReason;

  return (
    <Flex
      data-testid="order-replace-panel"
      direction="column"
      gap="3"
      p="3"
      width="22rem"
      minWidth="22rem"
      height="100%"
      overflow="auto"
      flexShrink="0"
      className="border-[color:var(--gray-a5)] border-l"
    >
      <ReplacePanelHeader
        isReplacing={isReplacing}
        onClose={onClose}
        orderId={order?.id}
        symbol={order?.symbol}
      />
      {order ? (
        <ReplacePanelAsset
          order={order}
          assetName={assetName}
          lastPrice={lastPrice}
        />
      ) : null}
      <ReplacePanelStatus
        isLoading={isLoading}
        isError={isError}
        errorMessage={errorMessage}
        eligibilityDisabledReason={eligibilityDisabledReason}
        onRetry={onRetry}
      />
      {showForm && controller ? (
        <ReplacePanelForm
          order={order}
          controller={controller}
          isReplacing={isReplacing}
          errorMessage={errorMessage}
        />
      ) : null}
    </Flex>
  );
};
